/**
 * Frances Design — DE GSAP Init
 * Registers ScrollTrigger, syncs Lenis, wires all scroll animations.
 * Must load AFTER gsap.min.js, ScrollTrigger.min.js, de-smooth-scroll.js.
 */
(function () {
  'use strict';

  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
    if (window.DEDebug) console.warn('[DE] GSAP or ScrollTrigger not loaded.');
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  // ---- Sync Lenis with GSAP ticker ----
  const lenis = window.__de_lenis;
  if (lenis) {
    // Hand the RAF loop to GSAP ticker (prevents double-loop)
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);
  }

  // ---- Activate CSS reveal system ----
  document.body.classList.add('de-animate-ready');

  // ---- Hero: char-by-char rise on load ----
  const heroEl = document.querySelector('[data-hero-headline]');
  if (heroEl) {
    // In Shopify's theme editor, the heading's innerHTML contains
    // <!-- BEGIN SETTING --> / <!-- END SETTING --> comment markers.
    // Reading innerHTML includes them and the char-splitter renders them
    // as visible text. Fix: always use textContent to get plain text only.
    // In design mode, skip the char animation entirely so the editor can
    // highlight and click-to-edit the heading without interference.
    if (window.Shopify && window.Shopify.designMode) {
      // Design mode: simple fade-in only, no DOM restructuring
      gsap.fromTo(heroEl,
        { opacity: 0, y: 12 },
        { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out', delay: 0.2 }
      );
    } else {
      // Live/preview: char-by-char rise using textContent (never innerHTML)
      const raw = heroEl.textContent.trim();
      const words = raw.split(/(\s+)/);
      heroEl.innerHTML = words.map((word) => {
        if (/^\s+$/.test(word)) return '<span style="display:inline-block;width:0.28em"> </span>';
        return '<span class="de-word" style="display:inline-block;overflow:hidden;vertical-align:bottom">' +
          word.split('').map((c) =>
            `<span class="de-char" style="display:inline-block">${c}</span>`
          ).join('') +
          '</span>';
      }).join('');

      const chars = heroEl.querySelectorAll('.de-char');
      gsap.fromTo(chars,
        { yPercent: 110, opacity: 0 },
        {
          yPercent: 0,
          opacity: 1,
          duration: 1.1,
          ease: 'power4.out',
          stagger: 0.018,
          delay: 0.25,
        }
      );
    }
  }

  // ---- Hero: subheading + CTAs fade up ----
  const heroSubs = document.querySelectorAll('[data-hero-sub]');
  if (heroSubs.length) {
    gsap.fromTo(heroSubs,
      { opacity: 0, y: 18 },
      {
        opacity: 1,
        y: 0,
        duration: 0.9,
        ease: 'power3.out',
        stagger: 0.14,
        delay: 1.3,
      }
    );
  }

  // ---- Generic section reveals: [data-animate] ----
  // Uses batch for performance — triggers when 10% above fold
  ScrollTrigger.batch('[data-animate]', {
    onEnter: (elements) => {
      gsap.to(elements, {
        opacity: 1,
        y: 0,
        duration: 0.85,
        ease: 'power3.out',
        stagger: { amount: 0.25, from: 'start' },
      });
    },
    start: 'top 90%',
    once: true,
  });

  // ---- Editorial split: image slides in, text fades up ----
  document.querySelectorAll('[data-editorial]').forEach((section) => {
    const img = section.querySelector('[data-editorial-image]');
    const content = section.querySelector('[data-editorial-content]');
    const dir = img && img.dataset.editorialImage === 'right' ? 80 : -80;

    if (img) {
      gsap.fromTo(img,
        { x: dir, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 1.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 78%',
            once: true,
          },
        }
      );
    }

    if (content) {
      gsap.fromTo(content,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.95,
          ease: 'power3.out',
          delay: 0.18,
          scrollTrigger: {
            trigger: section,
            start: 'top 78%',
            once: true,
          },
        }
      );
    }
  });

  // ---- Product grid: ScrollTrigger.batch() stagger ----
  document.querySelectorAll('[data-product-grid]').forEach((grid) => {
    const cards = Array.from(grid.querySelectorAll('.de-product-card'));
    if (!cards.length) return;

    // Pre-hide
    gsap.set(cards, { opacity: 0, y: 32 });

    ScrollTrigger.batch(cards, {
      onEnter: (batch) => {
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          duration: 0.75,
          ease: 'power3.out',
          stagger: 0.09,
        });
      },
      start: 'top 92%',
      once: true,
    });
  });

  // ---- Parallax: brand story image ----
  document.querySelectorAll('[data-parallax-container]').forEach((container) => {
    const inner = container.querySelector('[data-parallax]');
    if (!inner) return;

    gsap.to(inner, {
      yPercent: -18,
      ease: 'none',
      scrollTrigger: {
        trigger: container,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
      },
    });
  });

  // ---- Collection tiles: stagger on enter ----
  document.querySelectorAll('[data-collection-grid]').forEach((grid) => {
    const tiles = Array.from(grid.querySelectorAll('[data-animate-tile]'));
    if (!tiles.length) return;

    gsap.set(tiles, { opacity: 0, scale: 0.97 });

    ScrollTrigger.batch(tiles, {
      onEnter: (batch) => {
        gsap.to(batch, {
          opacity: 1,
          scale: 1,
          duration: 0.8,
          ease: 'power2.out',
          stagger: 0.1,
        });
      },
      start: 'top 88%',
      once: true,
    });
  });

  // ---- Scroll Story: Kindred-style dual-speed parallax ----
  // Both text elements start at the same top position.
  // Top text travels 35% of vh (slow); bottom text travels 55% of vh (fast).
  // Because bottom moves further, it ends up below the top text at scroll end.
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.querySelectorAll('.fd-scroll-story').forEach((section) => {
    if (prefersReducedMotion) return;
    if (window.Shopify && window.Shopify.designMode) return;

    const topText    = section.querySelector('.fd-scroll-story__top-text');
    const bottomText = section.querySelector('.fd-scroll-story__bottom-text');

    if (!topText || !bottomText) return;

    const stConfig = {
      trigger: section,
      start: 'top top',
      end: 'bottom bottom',
      scrub: 1.5,
      invalidateOnRefresh: true,
    };

    // Top text — moves down slowly (35% of viewport height)
    gsap.to(topText, {
      y: () => window.innerHeight * 0.35,
      ease: 'none',
      scrollTrigger: stConfig,
    });

    // Bottom text — moves down faster (55% of viewport height)
    // Ends up below top text because it travels further
    gsap.to(bottomText, {
      y: () => window.innerHeight * 0.55,
      ease: 'none',
      scrollTrigger: { ...stConfig },
    });
  });

})();
